import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Wizard from '../../components/common/Wizard';
import StepOrgDetail from './StepOrganizationDetail';
import StepAdminContact from './StepOrganizationAdminContact';
import MainPageLayout from '../../components/common/MainPageLayout';
import { createOrganization } from '../../services/apiServices';
import { useFeedbackContext } from '../../context';
import ROUTES from '../../constants/routes';
import { STEPS, ONBOARDING_ORGANIZATION_MESSAGES } from '../../constants/onBoardingOrganization';
import { useSessionFormPersistence } from '../../hooks/useSessionPersistance';
import { serviceLogger } from '../../utils/serviceLogger';

const FORM_KEY = 'orgWizardDraft';
const STEP_KEY = 'orgWizardStep';
const STATE_KEY = 'orgWizardState';
const EXPIRY_H = 24;

const OrganizationCreatePage = () => {
  // Form setup
  const methods = useForm();
  const { getValidData } = useSessionFormPersistence(FORM_KEY, methods, EXPIRY_H);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      const validForm = getValidData();
      methods.reset(validForm);
      setIsInitialized(true);
    }
  }, [getValidData, methods, isInitialized]);

  // Step state
  const savedStep = parseInt(sessionStorage.getItem(STEP_KEY), 10);
  const [currentStep, setCurrentStep] = useState(Number.isFinite(savedStep) ? savedStep : 0);

  // Step state rehydration
  const rawState = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
  const validState =
    rawState && Date.now() - rawState.__savedAt < EXPIRY_H * 3600 * 1000
      ? rawState
      : { completed: [], visited: [] };

  const { showToast } = useFeedbackContext();
  const { handleSubmit, formState } = methods;
  const navigate = useNavigate();

  // Final submit
  const submitOrganization = async (data) => {
    try {
      await createOrganization(data);

      // Clear session data
      [FORM_KEY, STEP_KEY, STATE_KEY].forEach((k) => sessionStorage.removeItem(k));

      showToast({
        variant: 'success',
        title: ONBOARDING_ORGANIZATION_MESSAGES.SUCCESS.TITLE,
        copy: ONBOARDING_ORGANIZATION_MESSAGES.SUCCESS.COPY,
      });
      navigate(ROUTES.ORGANIZATION_CONFIGURATION);
    } catch (err) {
      serviceLogger.error(err);
      showToast({
        variant: 'error',
        title: ONBOARDING_ORGANIZATION_MESSAGES.ERROR.TITLE,
        copy: ONBOARDING_ORGANIZATION_MESSAGES.ERROR.COPY,
      });
    }
  };

  return (
    <MainPageLayout>
      <h2 className="text-center text-[28px] font-bold mb-[12px]">Create your Organization</h2>
      <p className="text-center mb-[20px]">
        Step {currentStep + 1} of {STEPS.length}
      </p>

      <div className="flex justify-center">
        <div className="max-w-[626px] w-full">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(submitOrganization)} noValidate>
              <Wizard
                steps={STEPS}
                initialStep={currentStep}
                initialCompletedIds={validState.completed}
                initialVisitedIds={validState.visited}
                onStepChange={(idx) => {
                  setCurrentStep(idx);
                  sessionStorage.setItem(STEP_KEY, idx);
                }}
                onStatusChange={({ completedIds, visitedIds }) => {
                  sessionStorage.setItem(
                    STATE_KEY,
                    JSON.stringify({
                      completed: completedIds,
                      visited: visitedIds,
                      __savedAt: Date.now(),
                    }),
                  );
                }}
              >
                <StepOrgDetail />
                <StepAdminContact isSubmitting={formState.isSubmitting} />
              </Wizard>
            </form>
          </FormProvider>
        </div>
      </div>
    </MainPageLayout>
  );
};

export default OrganizationCreatePage;
